import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

interface MapShortcutsHelpProps {
  onClose: () => void;
}

type RowKey =
  | "prevNextAlert"
  | "trueDetection"
  | "falsePositive"
  | "closeCard"
  | "kpis"
  | "alerts"
  | "events"
  | "gps"
  | "video"
  | "images"
  | "toggleTimeline"
  | "toggleEditMode"
  | "closeTopLayer"
  | "toggleHelp";
type GroupKey = "alertCard" | "panels" | "timeline";

type Row = { keys: string[]; row: RowKey };
type Group = { group: GroupKey; rows: Row[] };

const GROUPS: Group[] = [
  {
    group: "alertCard",
    rows: [
      { keys: ["←", "→"], row: "prevNextAlert" },
      { keys: ["T", "R"], row: "trueDetection" },
      { keys: ["F"], row: "falsePositive" },
      { keys: ["Esc"], row: "closeCard" },
    ],
  },
  {
    group: "panels",
    rows: [
      { keys: ["K"], row: "kpis" },
      { keys: ["A"], row: "alerts" },
      { keys: ["E"], row: "events" },
      { keys: ["G"], row: "gps" },
      { keys: ["V"], row: "video" },
      { keys: ["I"], row: "images" },
    ],
  },
  {
    group: "timeline",
    rows: [
      { keys: ["T"], row: "toggleTimeline" },
      { keys: ["⇧", "E"], row: "toggleEditMode" },
      { keys: ["Esc"], row: "closeTopLayer" },
      { keys: ["?"], row: "toggleHelp" },
    ],
  },
];

const Key = ({ children }: { children: React.ReactNode }) => (
  <kbd className="inline-flex min-w-[1.5rem] items-center justify-center rounded border border-border bg-muted px-1.5 py-0.5 text-[11px] font-semibold leading-none text-foreground shadow-sm">
    {children}
  </kbd>
);

/** Centered overlay listing the `/map` keyboard shortcuts (toggled with `?`). */
const MapShortcutsHelp = ({ onClose }: MapShortcutsHelpProps) => {
  const { t } = useTranslation("map");

  return (
  <div
    className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 p-4 animate-in fade-in"
    onClick={onClose}
  >
    <div
      className="w-full max-w-lg rounded-xl border border-border bg-background/95 p-5 shadow-2xl backdrop-blur animate-in zoom-in-95"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold">{t("shortcuts.title")}</h2>
        <button
          type="button"
          aria-label={t("shortcuts.close")}
          onClick={onClose}
          className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-3">
        {GROUPS.map((group) => (
          <div key={group.group}>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t(`shortcuts.groups.${group.group}`)}
            </div>
            <ul className="space-y-1.5">
              {group.rows.map((row) => (
                <li key={row.row} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-muted-foreground">{t(`shortcuts.rows.${row.row}`)}</span>
                  <span className="flex shrink-0 items-center gap-0.5">
                    {row.keys.map((k) => (
                      <Key key={k}>{k}</Key>
                    ))}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </div>
  );
};

export default MapShortcutsHelp;
